/**
 * Shared parsing/rendering helpers for JAI's markdown-lite reply format
 * (**bold**, `- ` bullets, blank-line paragraph breaks). Split out from
 * JaiRichText.jsx (and reused by StreamingRichText.jsx) so both renderers
 * stay visually identical — and so this file can freely export plain
 * functions/constants without breaking Fast Refresh on the component files
 * that import them.
 */

// Stripped unconditionally: heading markers (the page already has its own
// header) and the [ACTION:x] / [NEEDS_LEAGUE] routing markers the shared
// Claude system prompt embeds for pages that turn them into buttons.
export function stripMarkers(content) {
  return (content || '')
    .replace(/\n?\[ACTION:[a-z_]+\]/gi, '')
    .replace(/\[NEEDS_LEAGUE\]/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .trim()
}

export function renderInline(text, boldColor) {
  const html = text.replace(
    /\*\*(.*?)\*\*/g,
    `<strong style="font-weight:700;color:${boldColor}">$1</strong>`
  )
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

// Groups lines into paragraph / bullet-list blocks so consecutive `- ` lines
// render as one real <ul> instead of one <p> per line.
export function toBlocks(content) {
  const blocks = []
  let list = null

  for (const raw of content.split('\n')) {
    const line = raw.trim()
    if (!line) { list = null; continue }

    const bulletMatch = line.match(/^[-•]\s+(.*)$/)
    if (bulletMatch) {
      if (!list) { list = { type: 'list', items: [] }; blocks.push(list) }
      list.items.push(bulletMatch[1])
    } else {
      list = null
      blocks.push({ type: 'p', text: line })
    }
  }

  return blocks
}

// Shared style objects, also used by StreamingRichText so a bullet/paragraph
// rendered mid-stream looks pixel-identical to one rendered here.
export const RICH_TEXT_STYLE = {
  list:      { margin: '0 0 14px', padding: 0, listStyle: 'none' },
  listItem:  { position: 'relative', paddingLeft: 16, marginBottom: 8 },
  dot:       { position: 'absolute', left: 0, top: '0.62em', width: 5, height: 5, borderRadius: '50%' },
  paragraph: (isLast) => ({ margin: isLast ? 0 : '0 0 14px' }),
}

// Renders parsed blocks as <ul>/<p> markup. `extraListItem` lets a caller
// (StreamingRichText) append one more, still-animating <li> onto the last
// block when it's a list that's actively being extended, so it stays one
// <ul> instead of splitting into two.
export function renderBlocks(blocks, { color, dotColor, trimLastMargin = true, extraListItem = null }) {
  return blocks.map((block, i) => {
    const isLast = trimLastMargin && i === blocks.length - 1
    const isLastBlock = i === blocks.length - 1

    if (block.type === 'list') {
      return (
        <ul key={i} style={RICH_TEXT_STYLE.list}>
          {block.items.map((item, j) => (
            <li key={j} style={RICH_TEXT_STYLE.listItem}>
              <span style={{ ...RICH_TEXT_STYLE.dot, background: dotColor }} />
              {renderInline(item, color)}
            </li>
          ))}
          {isLastBlock && extraListItem}
        </ul>
      )
    }

    return (
      <p key={i} style={RICH_TEXT_STYLE.paragraph(isLast)}>
        {renderInline(block.text, color)}
      </p>
    )
  })
}
